// based on https://github.com/vagran/dxf-viewer-example-src/blob/master/src/components/DxfViewer.vue

import React, { Component, useEffect, useState } from 'react';

import { Color } from 'three';

import { DxfViewer } from 'dxf-viewer';

import DxfViewerWorker from './DxfViewer.worker.js';

import './DxfViewer.css';
import { Button, useTheme } from '@mui/material';

const events = [
    "loaded",
    "cleared",
    "destroyed",
    "resized",
    "pointerdown",
    "pointerup",
    "viewChanged",
    "message"
];

const phases = {
    font: 'Fetching fonts...',
    fetch: 'Fetching file...',
    parse: 'Parsing file...',
    prepare: 'Preparing rendering data...'
};

const defaultOptions = {
    noControls: true,
    clearColor: new Color("#fff"),
    clearAlpha: 1.0,
    autoResize: false,
    colorCorrection: true,
    sceneOptions: {
        wireframeMesh: true
    },
    retainParsedDxf: true,
};

const allUnits = {
    0: 1, // Unitless
    1: 2.54, // Inches
    // 2 = Feet;
    3: 1.609344 * 1000 * 100, // Miles
    4: 1 / 10, // Millimeters
    5: 1, // Centimeters
    6: 1 * 100, // Meters
    7: 1 * 1000 * 100, // Kilometers
    // 8 = Microinches;
    // 9 = Mils;
    // 10 = Yards;
    // 11 = Angstroms;
    // 12 = Nanometers;
    // 13 = Microns;
    // 14 = Decimeters;
    // 15 = Decameters;
    // 16 = Hectometers;
    // 17 = Gigameters;
    // 18 = Astronomical units;
    // 19 = Light years;
    // 20 = Parsecs
};

export class DxfViewerPro extends DxfViewer {
    padding = 0;
    zoom = 1;

    scale = 1;

    offsetX = 0;
    offsetY = 0;
    
    SetScale(value) {
        this.scale = value || 1;
        this.camera.zoom = this.scale * this.zoom;
        this.camera.updateMatrix();
        this.camera.updateProjectionMatrix();
    }
    
    SetOffset(x, y) {
        this.offsetX = x || 0;
        this.offsetY = y || 0;
    }

    SetPadding(value) {
        this.padding = value;
    }

    SetZoom(value) {
        this.zoom = value || 1;
        this.camera.zoom = this.scale * this.zoom;
        this.camera.updateMatrix();
        this.camera.updateProjectionMatrix();
    }

    GetSize() {
        const { minX, minY, maxX, maxY } = this.bounds;
        return {
            width: maxX - minX,
            height: maxY - minY
        };
    }

    GetSideOffset() {
        const { minX, minY } = this.bounds;
        const { x, y } = this.origin;
        return {
            x: x - minX,
            y: y - minY
        };
    }

    SetPosition(x, y) {
        const s = this.scale;

        let w = 1; // width

        let fx = -x;
        let fy = y;

        if (this.bounds && this.origin) {
            const { minX, maxX } = this.bounds;
            const { x: orx, y: ory } = this.origin;

            const cw = this.canvasWidth;
            const ch = this.canvasHeight;

            const car = ch / cw;

            w = maxX - minX;

            fx = fx * w / s - this.offsetX - orx;
            fy = fy * w * car / s + this.offsetY + ory;
        }

        this.camera.position.set(fx, fy, 1);
        this.camera.updateMatrix();
        this.camera.updateProjectionMatrix();
    }

    GetBounds() {
        return this.bounds;
    }

    GetUnitsScale() {
        if (!this.parsedDxf) return 1;
        const units = this.parsedDxf.header.$INSUNITS;
        const scale = allUnits[units];
        return scale;
    }

    FitView(minX, maxX, minY, maxY) {
        const width = maxX - minX;
        const height = maxY - minY;
        
        let resultWidth = width;

        if (resultWidth <= Number.MIN_VALUE * 2) {
            resultWidth = 1;
        }

        const center = { x: minX + width / 2, y: minY + height / 2 };

        this.SetView(center, resultWidth * (1 + this.padding));
    }

    ReFit() {
        if (!this.bounds || !this.origin) return;
        const { minX, maxX, minY, maxY } = this.bounds;
        const { x, y } = this.origin;
        this.FitView(minX - x, maxX - x, minY - y, maxY - y);
    }

    _CreateControls() {
        if (this.options.noControls) return;
        super._CreateControls();
    }
}

const getCssColor = (value) => {
    let s = value.toString(16)
    while (s.length < 6) { s = "0" + s }
    return "#" + s
};

export const DxfLayers = ({ layers, onChanged, selected }) => {
    const [visible, setVisible] = useState(false);
    const [values, setValues] = useState({});
    const [shown, setShown] = useState(true);

    const theme = useTheme();

    useEffect(() => {
        const newValues = {};
        layers.forEach(layer => {
            if (layer) newValues[layer.name] = values[layer.name] || true;
        });
        setValues(newValues);
    }, [layers]); // eslint-disable-line react-hooks/exhaustive-deps

    const safeOnChanged = (v) => {
        if (typeof onChanged === 'function') onChanged(v);
    }

    const handleChange = (name) => {
        setValues(v => ({ ...v, [name]: !v[name] })); // invert
        const newValues = { ...values, [name]: !values[name] };
        safeOnChanged(newValues);
    };

    const toggleAll = () => {
        const newValues = {};
        Object.keys(values).forEach(k => {
            newValues[k] = !shown;
        })
        setShown(!shown);
        setValues(newValues);
        safeOnChanged(newValues);
    };

    return (
        <div className="dxf-viewer-layers">
            <div className="dxf-viewer-layers-buttons" style={{backgroundColor: theme.palette.background.secondary, borderRadius: '5px'}}>
                <Button
                    variant='contained'
                    style={{backgroundColor: theme.palette.background.secondaryButton, color: theme.palette.white.main}}
                    //className="dxf-viewer-layers-button"
                    onClick={() => setVisible(!visible)}
                >
                    {visible ? 'Close' : 'Layers'}
                </Button>
                {visible && (
                    <Button
                        variant="contained"
                        style={{backgroundColor: theme.palette.background.secondaryButton, color: theme.palette.white.main}}
                        //className="dxf-viewer-layers-button"
                        onClick={toggleAll}
                    >
                        {shown ? 'Hide' : 'Show'} All
                    </Button>
                )}
            </div>
            {visible && layers.map(layer => (
                <div key={layer.name} style={{backgroundColor: theme.palette.background.secondary}}>
                    <input
                        type="checkbox"
                        id={`layer_${layer.name}`}
                        checked={values[layer.name]}
                        onChange={() => handleChange(layer.name)}
                        value={layer.name}
                    />
                    <label
                        htmlFor={`layer_${layer.name}`}
                        className="dxf-viewer-layer"
                        style={{color: selected == layer.name ? 'red' : 'inherit'}}
                    >
                        <span
                            className="dxf-viewer-layer-color" 
                            style={{ backgroundColor: getCssColor(layer.color) }}
                        />
                        {layer.displayName || layer.name}
                    </label>
                </div>
            ))}
        </div>
    );
};

class DXF extends Component {
    state = {
        isLoading: false,
        progress: null,
        currentPhase: null,
        error: null,
    };

    viewer = null;

    options = defaultOptions;

    fonts = null;

    container = React.createRef();

    canvas = null;

    componentDidMount() {
        const { url, width, height, fonts, options } = this.props;

        if (options) this.options = { ...defaultOptions, ...options };

        this.createViewer();

        if (this.viewer) {
            this.viewer.SetSize(width, height);
            if (url) this.load(url, fonts).then(this.loaded);
        }
    }

    componentDidUpdate(prevProps) {
        const { url, x, y, zoom, width, height, scale, ox, oy, fonts, options, layers } = this.props;

        /*if (!this.viewer) return console.error('no viewer');*/

        if (width !== prevProps.width || height !== prevProps.height) {
            this.viewer.SetSize(width, height);
            this.viewer.ReFit();
            this.viewer.SetZoom(zoom);
            this.viewer.SetScale(scale);
            this.viewer.SetScale(scale);
            this.viewer.SetOffset(ox, oy);
            this.viewer.SetPosition(x, y);
        }

        if (zoom !== prevProps.zoom) {
            this.viewer.SetZoom(zoom);
        }

        if (scale !== prevProps.scale) {
            this.viewer.SetScale(scale);
        }

        if (ox !== prevProps.ox || oy !== prevProps.oy) {
            this.viewer.SetOffset(ox, oy);
            this.viewer.SetPosition(x, y);
        }

        if (x !== prevProps.x || y !== prevProps.y) {
            this.viewer.SetPosition(x, y);
        }

        this.viewer.Render();

        if (url !== prevProps.url) {
            this.load(url, fonts).then(this.loaded);
        }

        if (fonts !== prevProps.fonts) {
            this.load(url, fonts).then(this.loaded);
        }

        if (JSON.stringify(options) !== JSON.stringify(prevProps.options)) {
            if (options) this.options = { ...defaultOptions, ...options };
            this.destroyViewer();
            this.createViewer();
            this.load(url, fonts).then(this.loaded);
        }

        if (JSON.stringify(layers) !== JSON.stringify(prevProps.layers)) {
            Object.keys(layers).forEach((key) => {
                this.viewer.ShowLayer(key, layers[key]);
            });
        }
    }

    componentWillUnmount() {
        this.destroyViewer();
    }

    createViewer() {
        const { debug } = this.props;
        const cont = this.container.current;
        if (cont !== null) {
            this.viewer = new DxfViewerPro(cont, this.options);
            if (debug) for (const eventName of events) {
                //this.viewer.Subscribe(eventName, e => console.info("dxf-" + eventName, e));
            }
        } else {
            /*console.error('no container');*/
        }
    }

    loaded = () => {
        const { onLoaded, zoom, x, y, width, height, scale, ox, oy } = this.props;
        this.viewer.SetSize(width, height);
        this.viewer.ReFit();
        this.viewer.SetZoom(zoom);
        this.viewer.SetScale(scale);
        this.viewer.SetOffset(ox, oy);
        this.viewer.SetPosition(x, y);
        this.viewer.Render();
        if (typeof onLoaded === 'function') {
            onLoaded({
                layers: this.viewer.GetLayers(),
                size: this.viewer.GetSize(),
                //offset: this.viewer.GetSideOffset(),
                scale: this.viewer.GetUnitsScale(),
            });
        }
    }

    cleanContainer() {
        const parent = this.container.current;
        while (parent.firstChild) {
            parent.firstChild.remove();
        }
    }

    destroyViewer() {
        this.cleanContainer();
        if (this.viewer) this.viewer.Destroy();
        this.viewer = null;
    }

    onProgress(phase, size, totalSize) {
        const { currentPhase } = this.state;
        if (phase !== currentPhase) {
            this.setState({ currentPhase: phase });
        }
        if (totalSize === null || totalSize === 0) {
            this.setState({ progress: null });
        } else {
            this.setState({ progress: Math.floor(size / totalSize * 100) });
        }
    }

    async load(url, fonts) {
        if (url) {
            this.setState({ error: null, isLoading: true });
            try {
                await this.viewer.Load({
                    url,
                    fonts,
                    workerFactory: DxfViewerWorker,
                    progressCbk: this.onProgress.bind(this)
                });
                this.canvas = this.viewer.GetCanvas();
            } catch (error) {
                /*console.warn(error);*/
                this.setState({ error: error.toString() });
            } finally {
                this.setState({ isLoading: false, currentPhase: null, progress: null });
            }
        } else {
            this.viewer.Clear();
            this.setState({ error: null, isLoading: false, currentPhase: null, progress: null });
        }
    }

    render () {
        const { currentPhase, progress, error } = this.state;
        const { debug, /*zoom, x, y*/ } = this.props;

        return (
            <React.Fragment>
                <div ref={this.container} className="dxf-viewer-container"></div>
                <div className="dxf-viewer-info">
                    {debug && (
                        <pre style={{ margin: 0 }}>
                            {/*JSON.stringify({ zoom, x, y }, undefined, 2)*/}
                        </pre>
                    )}
                    {error && (<div>{error}</div>)}
                    {currentPhase && (
                        <div>
                            {phases[currentPhase] || ''}
                            {progress && (<span> {progress}%</span>)}
                        </div>
                    )}
                </div>
            </React.Fragment>  
        );
    }
};

DXF.defaultProps = {
    url: null,
    options: {},
    fonts: null,
    x: 0,
    y: 0,
    zoom: 1,
    width: 300,
    height: 300,
    scale :1,
    ox: 0,
    oy: 0,
    debug: false,
    onLoaded: null,
    layers: {}
};

export default DXF;